'use client';

import { c as _c } from "react/compiler-runtime";
import { LayoutGroup, motion, useReducedMotion } from 'motion/react';
import type { ComponentProps, Context, ReactNode } from 'react';
import * as React from 'react';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { Tabs as ShadcnTabs, TabsContent as ShadcnTabsContent, TabsList as ShadcnTabsList, TabsTrigger as ShadcnTabsTrigger } from '@/components/ui/tabs';
import { MOTION_POSITION_SMALL, MOTION_TAB_LINE_UNDERLINE } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Selection + list-variant context — enables Motion shared-layout indicators
// (line underline, segmented sliding pill) without modifying the stock
// primitive (FQ-111).
// ---------------------------------------------------------------------------

type TabsSelectionContextValue = {
  controlledSelectedValue: string | undefined;
};
const TabsSelectionContext: Context<TabsSelectionContextValue | null> = React.createContext<TabsSelectionContextValue | null>(null);
type TabsListVariantContextValue = 'default' | 'line' | 'segmented';
const TabsListVariantContext: Context<TabsListVariantContextValue | null> = React.createContext<TabsListVariantContextValue | null>(null);

/**
 * LayoutGroup id shared by every trigger inside a single segmented TabsList,
 * so the motion sliding pill animates between the active triggers via shared
 * layout. Distinct ids per list so multiple segmented strips on one page
 * don't cross-animate.
 */
const TabsSegmentedGroupContext: Context<string | null> = React.createContext<string | null>(null);

// ---------------------------------------------------------------------------
// Public surface — all app code imports from here only (Biome noRestrictedImports).
// Stock typography/tokens + animated line underline for controlled line tabs.
// ---------------------------------------------------------------------------

// Morfi semantic tokens layered over stock tabs.tsx (which uses rounded-md / text-sm).
// Line-variant trigger has no visible background so the radius is invisible there;
// applying `rounded-control` universally is harmless and keeps the wrapper simple.
const TABS_TRIGGER_TOKENS = 'rounded-control text-body' as const;
/** Overlays stock inactive label — tertiary semantic reads below active ink (FQ-111 keeps tabs.tsx stock). */
const TABS_TRIGGER_INACTIVE_LABEL = 'text-foreground-tertiary dark:text-foreground-tertiary dark:hover:text-foreground-primary';
const TABS_LIST_LINE_GAP = 'gap-0' as const;

// Line-variant tab strips overflow horizontally on mobile when the label set
// is wide (e.g. 6-tab portfolio: PnL · Positions · Recent Activity · Theses ·
// Gifts · Referrals). Stock triggers are `flex-1 whitespace-nowrap` with no
// `min-w-0`, so they refuse to shrink below their text width — the row leaks
// past its container and shifts the entire host page horizontally on iOS.
//
// Convert the row into a horizontal scroll strip on `<sm`: keep each trigger
// at natural content width (override `flex-1` via `flex-none`), let the row
// grow as wide as its content needs, and scroll horizontally inside the
// parent's width. `scrollbar-none` hides the chrome (utility lives in
// globals.css). `[&::-webkit-scrollbar]:hidden` is a belt-and-braces
// fallback for older Safari.
const TABS_LIST_LINE_OVERFLOW = 'max-w-full overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden [&>*]:flex-none' as const;

/**
 * Segmented variant — compact rectangular segmented control: 32 px tall (`h-8`),
 * 8 px outer radius, 6 px inner pill radius, semantic background tokens. Designed
 * to host an animated sliding pill indicator (handled in TabsTrigger). The
 * 8 px / 32 px values are intentional product dimensions for this variant
 * and don't map onto the existing `--radius-{item|control}` (4 / 12 px) or
 * `h-9` (36 px) tokens — kept arbitrary so no new design token is required.
 */
const TABS_LIST_SEGMENTED_SM = 'group-data-[orientation=horizontal]/tabs:h-7 w-full min-w-0 rounded-[0.5rem] gap-0.5 p-0.5 bg-background-tertiary' as const;
/**
 * Segmented variant — `md` size: 40 px tall, 10 px outer radius, 8 px inner
 * pill radius, body-size labels. Use when the tab strip is the primary
 * navigation of a panel and needs the visual weight to match larger headers.
 */
const TABS_LIST_SEGMENTED_MD = 'group-data-[orientation=horizontal]/tabs:h-10 w-full min-w-0 rounded-[0.625rem] gap-0.5 p-1 bg-background-tertiary' as const;
/** Full-width segmented row: triggers keep `flex-1` so segments share row width (`[&>*]:flex-none` omitted).
 * `overflow-y-hidden` is explicit — `overflow-x-auto` alone coerces `overflow-y` from `visible` to `auto`
 * (per CSS overflow spec), which makes the list vertically scrollable if anything causes even 1–2 px of
 * vertical overflow. The implicit scroll shifts Motion's layout-measurement baseline and misaligns the pill. */
const TABS_LIST_SEGMENTED_OVERFLOW = 'max-w-full overflow-x-auto overflow-y-hidden scrollbar-none [&::-webkit-scrollbar]:hidden' as const;
type ShadcnTabsListVariant = 'default' | 'line';
type TabsListVariant = ShadcnTabsListVariant | 'segmented';
export type TabsListSize = 'sm' | 'md';
type TabsListProps = Omit<ComponentProps<typeof ShadcnTabsList>, 'variant'> & {
  variant?: TabsListVariant | undefined;
  /** Segmented-variant size. `sm` (default) = 28 px / caption text. `md` = 40 px / body text. Ignored by other variants. */
  size?: TabsListSize | undefined;
};
type TabsTriggerProps = ComponentProps<typeof ShadcnTabsTrigger>;
type TabsContentProps = ComponentProps<typeof ShadcnTabsContent>;
export function Tabs(props) {
  const $ = _c(7);
  const {
    value
  } = props;
  const t0 = value === undefined ? undefined : value;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      controlledSelectedValue: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectionContextValue = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <ShadcnTabs {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== selectionContextValue || $[5] !== t2) {
    t3 = <TabsSelectionContext.Provider value={selectionContextValue}>{t2}</TabsSelectionContext.Provider>;
    $[4] = selectionContextValue;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function TabsList(t0) {
  const $ = _c(27);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const variant = t1 === undefined ? "default" : t1;
  const size = t2 === undefined ? "sm" : t2;
  const layoutGroupId = React.useId();
  const underlyingVariant = variant === "segmented" ? "default" : variant;
  const segmentedSizeClasses = variant === "segmented" ? size === "md" ? TABS_LIST_SEGMENTED_MD : TABS_LIST_SEGMENTED_SM : undefined;
  const t3 = variant === "line" && TABS_LIST_LINE_GAP;
  const t4 = variant === "line" && TABS_LIST_LINE_OVERFLOW;
  const t5 = variant === "segmented" && TABS_LIST_SEGMENTED_OVERFLOW;
  let t6;
  if ($[5] !== className || $[6] !== segmentedSizeClasses || $[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = cn(t3, t4, segmentedSizeClasses, t5, className);
    $[5] = className;
    $[6] = segmentedSizeClasses;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== props || $[12] !== size || $[13] !== t6 || $[14] !== underlyingVariant) {
    t7 = <ShadcnTabsList className={t6} variant={underlyingVariant} data-size={size} {...props} />;
    $[11] = props;
    $[12] = size;
    $[13] = t6;
    $[14] = underlyingVariant;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const list = t7;
  const segmentedGroupId = variant === "segmented" ? `morfi-tabs-segmented-${layoutGroupId}` : null;
  let t8;
  if ($[16] !== layoutGroupId || $[17] !== list || $[18] !== segmentedGroupId || $[19] !== variant) {
    t8 = variant === "line" ? <LayoutGroup id={`morfi-tabs-line-${layoutGroupId}`}>{list}</LayoutGroup> : variant === "segmented" ? <LayoutGroup id={segmentedGroupId ?? `morfi-tabs-segmented-${layoutGroupId}`}>{list}</LayoutGroup> : list;
    $[16] = layoutGroupId;
    $[17] = list;
    $[18] = segmentedGroupId;
    $[19] = variant;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const layoutWrapped = t8;
  let t9;
  if ($[21] !== layoutWrapped || $[22] !== segmentedGroupId) {
    t9 = <TabsSegmentedGroupContext.Provider value={segmentedGroupId}>{layoutWrapped}</TabsSegmentedGroupContext.Provider>;
    $[21] = layoutWrapped;
    $[22] = segmentedGroupId;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9 || $[25] !== variant) {
    t10 = <TabsListVariantContext.Provider value={variant}>{t9}</TabsListVariantContext.Provider>;
    $[24] = t9;
    $[25] = variant;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
export function TabsTrigger(t0) {
  const $ = _c(25);
  let children;
  let className;
  let props;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      value,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = value;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    value = $[4];
  }
  const selectionCtx = React.use(TabsSelectionContext);
  const listVariant = React.use(TabsListVariantContext);
  const segmentedGroupId = React.use(TabsSegmentedGroupContext);
  const lineUsesMotion = selectionCtx !== null && selectionCtx.controlledSelectedValue !== undefined;
  const showMotionLineUnderline = listVariant === "line" && lineUsesMotion && value !== undefined && selectionCtx.controlledSelectedValue === value;
  const showMotionSegmentedPill = listVariant === "segmented" && lineUsesMotion && value !== undefined && selectionCtx.controlledSelectedValue === value && segmentedGroupId !== null;
  const lineUnderlineAfterClasses = lineUsesMotion ? "group-data-[variant=line]/tabs-list:after:content-none" : undefined;
  let T0;
  let t1;
  let t2;
  if ($[5] !== className || $[6] !== lineUnderlineAfterClasses || $[7] !== listVariant || $[8] !== value) {
    const segmentedTriggerClasses = listVariant === "segmented" ? cn("isolate h-full min-w-0 flex-1 px-3 py-0 rounded-[0.375rem] text-caption", "group-data-[size=md]/tabs-list:rounded-[0.5rem] group-data-[size=md]/tabs-list:px-4 group-data-[size=md]/tabs-list:text-body group-data-[size=md]/tabs-list:font-medium", "data-[state=active]:bg-transparent data-[state=active]:shadow-none", "group-data-[variant=default]/tabs-list:data-[state=active]:shadow-none", "dark:data-[state=active]:bg-transparent dark:data-[state=active]:border-transparent") : undefined;
    T0 = ShadcnTabsTrigger;
    t1 = value;
    t2 = cn(TABS_TRIGGER_TOKENS, TABS_TRIGGER_INACTIVE_LABEL, lineUnderlineAfterClasses, segmentedTriggerClasses, className);
    $[5] = className;
    $[6] = lineUnderlineAfterClasses;
    $[7] = listVariant;
    $[8] = value;
    $[9] = T0;
    $[10] = t1;
    $[11] = t2;
  } else {
    T0 = $[9];
    t1 = $[10];
    t2 = $[11];
  }
  let t3;
  if ($[12] !== showMotionLineUnderline) {
    t3 = showMotionLineUnderline ? <motion.span layoutId="morfi-tabs-line-underline" transition={MOTION_TAB_LINE_UNDERLINE} className={cn("pointer-events-none absolute bg-foreground-primary", "group-data-[orientation=horizontal]/tabs:inset-x-0 group-data-[orientation=horizontal]/tabs:bottom-[-5px] group-data-[orientation=horizontal]/tabs:h-0.5", "group-data-[orientation=vertical]/tabs:inset-y-0 group-data-[orientation=vertical]/tabs:-right-1 group-data-[orientation=vertical]/tabs:w-0.5")} aria-hidden="true" /> : null;
    $[12] = showMotionLineUnderline;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== segmentedGroupId || $[15] !== showMotionSegmentedPill) {
    t4 = showMotionSegmentedPill ? <motion.span layoutId={`${segmentedGroupId}-pill`} transition={MOTION_POSITION_SMALL} className="pointer-events-none absolute inset-0 -z-10 rounded-[0.375rem] border border-border-secondary bg-background-primary group-data-[size=md]/tabs-list:rounded-[0.5rem]" aria-hidden="true" /> : null;
    $[14] = segmentedGroupId;
    $[15] = showMotionSegmentedPill;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== T0 || $[18] !== children || $[19] !== props || $[20] !== t1 || $[21] !== t2 || $[22] !== t3 || $[23] !== t4) {
    t5 = <T0 value={t1} className={t2} {...props}>{children}{t3}{t4}</T0>;
    $[17] = T0;
    $[18] = children;
    $[19] = props;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
}
export function TabsContent(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ShadcnTabsContent {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

// ---------------------------------------------------------------------------
// Hybrid API — flat for simple cases, compound for richer cases.
// ---------------------------------------------------------------------------

export type AppTabsVariant = 'default' | 'line' | 'segmented';
export interface AppTabsItem {
  value: string;
  label: ReactNode;
  /**
   * Optional leading icon. Honored by the default (pill) variant — rendered
   * to the left of the label, brand-colored when active. The line variant
   * ignores this; line consumers should compose icons inside `label` directly.
   */
  icon?: IconSvgElement | undefined;
  disabled?: boolean | undefined;
  content?: ReactNode | undefined;
}
export interface AppTabsFlatProps {
  value: string;
  onValueChange: (value: string) => void;
  variant?: AppTabsVariant | undefined;
  items: ReadonlyArray<AppTabsItem>;
  listClassName?: string | undefined;
  contentClassName?: string | undefined;
}
interface AppTabsComponent {
  (props: AppTabsFlatProps): React.ReactNode;
  Root: typeof Tabs;
  List: typeof TabsList;
  Trigger: typeof TabsTrigger;
  Content: typeof TabsContent;
}
function AppTabsImpl(t0) {
  const $ = _c(45);
  const {
    value,
    onValueChange,
    variant: t1,
    items,
    listClassName,
    contentClassName
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const layoutId = React.useId();
  if (variant === "line" || variant === "segmented") {
    let t2;
    if ($[0] !== items || $[1] !== variant) {
      let t3;
      if ($[3] !== variant) {
        t3 = item => <TabsTrigger key={item.value} value={item.value} {...item.disabled !== undefined ? {
          disabled: item.disabled
        } : {}}>{variant === "segmented" && item.icon !== undefined ? <Icon icon={item.icon} className="size-4 shrink-0" aria-hidden="true" /> : null}{item.label}</TabsTrigger>;
        $[3] = variant;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      t2 = items.map(t3);
      $[0] = items;
      $[1] = variant;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[5] !== listClassName || $[6] !== t2 || $[7] !== variant) {
      t3 = <TabsList variant={variant} className={listClassName}>{t2}</TabsList>;
      $[5] = listClassName;
      $[6] = t2;
      $[7] = variant;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== contentClassName || $[10] !== items) {
      let t5;
      if ($[12] !== contentClassName) {
        t5 = item_0 => item_0.content !== undefined && <TabsContent key={item_0.value} value={item_0.value} className={contentClassName}>{item_0.content}</TabsContent>;
        $[12] = contentClassName;
        $[13] = t5;
      } else {
        t5 = $[13];
      }
      t4 = items.map(t5);
      $[9] = contentClassName;
      $[10] = items;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[14] !== onValueChange || $[15] !== t3 || $[16] !== t4 || $[17] !== value) {
      t5 = <Tabs value={value} onValueChange={onValueChange}>{t3}{t4}</Tabs>;
      $[14] = onValueChange;
      $[15] = t3;
      $[16] = t4;
      $[17] = value;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    return t5;
  }
  let t2;
  if ($[19] !== items || $[20] !== value) {
    let t3;
    if ($[22] !== value) {
      t3 = item_1 => item_1.value === value;
      $[22] = value;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    t2 = items.find(t3);
    $[19] = items;
    $[20] = value;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  const activeItem = t2;
  let t3;
  if ($[24] !== listClassName) {
    t3 = cn("flex w-full items-center gap-1 rounded-pill bg-background-tertiary p-1", listClassName);
    $[24] = listClassName;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  let t4;
  if ($[26] !== items || $[27] !== layoutId || $[28] !== onValueChange || $[29] !== value) {
    let t5;
    if ($[31] !== layoutId || $[32] !== onValueChange || $[33] !== value) {
      t5 = item_2 => <AppTabsPillTrigger key={item_2.value} item={item_2} isActive={item_2.value === value} layoutId={`morfi-tabs-pill-${layoutId}`} onSelect={onValueChange} />;
      $[31] = layoutId;
      $[32] = onValueChange;
      $[33] = value;
      $[34] = t5;
    } else {
      t5 = $[34];
    }
    t4 = items.map(t5);
    $[26] = items;
    $[27] = layoutId;
    $[28] = onValueChange;
    $[29] = value;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  let t5;
  if ($[35] !== t3 || $[36] !== t4) {
    t5 = <div role="tablist" className={t3}>{t4}</div>;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  let t6;
  if ($[38] !== activeItem || $[39] !== contentClassName || $[40] !== layoutId) {
    t6 = activeItem?.content !== undefined && <div role="tabpanel" aria-labelledby={`${layoutId}-${activeItem.value}`} className={cn("mt-3", contentClassName)}>{activeItem.content}</div>;
    $[38] = activeItem;
    $[39] = contentClassName;
    $[40] = layoutId;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  let t7;
  if ($[42] !== t5 || $[43] !== t6) {
    t7 = <div>{t5}{t6}</div>;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
  } else {
    t7 = $[44];
  }
  return t7;
}
interface AppTabsPillTriggerProps {
  item: AppTabsItem;
  isActive: boolean;
  layoutId: string;
  onSelect: (value: string) => void;
}
function AppTabsPillTrigger(t0) {
  const $ = _c(25);
  const {
    item,
    isActive,
    layoutId,
    onSelect
  } = t0;
  const prefersReducedMotion = useReducedMotion();
  let t1;
  if ($[0] !== prefersReducedMotion) {
    t1 = prefersReducedMotion === true ? {
      duration: 0
    } : MOTION_POSITION_SMALL;
    $[0] = prefersReducedMotion;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const indicatorTransition = t1;
  const itemDisabled = item.disabled === true;
  let t2;
  if ($[2] !== item.value || $[3] !== itemDisabled || $[4] !== onSelect) {
    t2 = () => {
      if (!itemDisabled) {
        onSelect(item.value);
      }
    };
    $[2] = item.value;
    $[3] = itemDisabled;
    $[4] = onSelect;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = isActive ? "text-foreground-primary" : "text-foreground-tertiary hover:text-foreground-primary";
  const t4 = itemDisabled && "pointer-events-none opacity-50";
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = cn("group relative inline-flex h-9 flex-1 min-w-0 appearance-none items-center justify-center gap-1.5 rounded-pill px-3 text-body font-medium whitespace-nowrap outline-none transition-colors focus-visible:ring-2 focus-visible:ring-border-primary", t3, t4);
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== indicatorTransition || $[10] !== isActive || $[11] !== layoutId) {
    t6 = isActive && <motion.span layoutId={layoutId} className="pointer-events-none absolute inset-0 z-0 rounded-pill bg-background-primary shadow-elevation-inset" transition={indicatorTransition} aria-hidden="true" />;
    $[9] = indicatorTransition;
    $[10] = isActive;
    $[11] = layoutId;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== item.icon) {
    t7 = item.icon !== undefined && <Icon icon={item.icon} className="relative z-10 size-4 shrink-0" aria-hidden="true" />;
    $[13] = item.icon;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== item.label) {
    t8 = <span className="relative z-10 truncate">{item.label}</span>;
    $[15] = item.label;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isActive || $[18] !== itemDisabled || $[19] !== t2 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <button type="button" role="tab" aria-selected={isActive} disabled={itemDisabled} onClick={t2} className={t5}>{t6}{t7}{t8}</button>;
    $[17] = isActive;
    $[18] = itemDisabled;
    $[19] = t2;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
export const AppTabs: AppTabsComponent = Object.assign(AppTabsImpl, {
  Root: Tabs,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent
});